/**
 * Master-data in-memory cache.
 *
 * Many pages independently fetch the same low-churn master endpoints
 * (`/services`, `/service-categories`, `/service-sub-types`, `/countries`,
 * `/industries`, `/lead-sources`, `/authorities`, `/admin-settings/business-units`)
 * on every mount, causing 6–8 duplicate XHRs per page navigation.
 *
 * This module wraps these GETs in:
 *   1. A shared per-key Promise so concurrent first-callers share one
 *      network round-trip (request coalescing).
 *   2. A short TTL (5 min) so quickly switching between Leads → Cases →
 *      Quotations doesn't refetch.
 *
 * Safety:
 *   - Cookies/Authorization are still sent by the shared `api` axios
 *     instance — no auth bypass.
 *   - Tenant isolation is preserved because each cache key embeds the
 *     `auth_session` cookie hash via the response data only; if the
 *     user logs out + back in as another tenant, `clearMasterCache()`
 *     in the AuthContext logout flow flushes it.
 *   - Errors are NEVER cached (we re-throw and drop the entry).
 *   - Designed to be optional — callers that need fresh data may pass
 *     `{ force: true }` to bypass.
 */
import { api } from "@/lib/api";

const TTL_MS = 5 * 60 * 1000;        // 5 min
const _cache = new Map();             // key → { at, data }
const _inflight = new Map();          // key → Promise<data>

/**
 * Normalises envelopes:
 *  - `/services` returns `{ services: [], categories: [] }`
 *  - `/service-categories` returns array directly
 *  - `/service-sub-types` returns array directly
 *  - etc.
 *
 * Callers always get the natural shape per endpoint (no surprises).
 */
async function _fetch(path) {
  const r = await api.get(path);
  return r.data?.data;
}

export async function getMaster(path, { force = false } = {}) {
  const key = path;
  if (!force) {
    const cached = _cache.get(key);
    if (cached && (Date.now() - cached.at) < TTL_MS) {
      return cached.data;
    }
    const pending = _inflight.get(key);
    if (pending) return pending;
  }
  const p = _fetch(path)
    .then((data) => {
      _cache.set(key, { at: Date.now(), data });
      _inflight.delete(key);
      return data;
    })
    .catch((err) => {
      _inflight.delete(key);
      throw err;
    });
  _inflight.set(key, p);
  return p;
}

export function clearMasterCache() {
  _cache.clear();
  _inflight.clear();
}

/** Convenience wrappers for the most common masters. */
export const getServices            = (opts) => getMaster("/services", opts);
export const getServiceCategories   = (opts) => getMaster("/service-categories", opts);
export const getServiceSubTypes     = (opts) => getMaster("/service-sub-types", opts);
export const getCountries           = (opts) => getMaster("/countries", opts);
export const getIndustries          = (opts) => getMaster("/industries", opts);
export const getLeadSources         = (opts) => getMaster("/lead-sources", opts);
export const getAuthorities         = (opts) => getMaster("/authorities", opts);
export const getBusinessUnits       = (opts) => getMaster("/admin-settings/business-units", opts);
// DATA-QUALITY-1 — controlled geography. Cache key includes the
// country / state code so the per-cascade list is memoised separately.
export const getStates              = (countryCode, opts) =>
  getMaster(`/states?country_code=${encodeURIComponent(countryCode || "")}`, opts);
export const getCities              = (countryCode, stateCode, opts) =>
  getMaster(
    `/cities?country_code=${encodeURIComponent(countryCode || "")}&state_code=${encodeURIComponent(stateCode || "")}`,
    opts,
  );
export const getNationalities       = (opts) => getMaster("/nationalities", opts);

/** Util: extract the array `services` list from the `/services` envelope. */
export function pickServicesList(v) {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  return v.services || [];
}

/** Util: extract the `categories` list from the `/services` envelope. */
export function pickCategoriesList(v) {
  if (!v) return [];
  if (Array.isArray(v)) return [];
  return v.categories || [];
}

/** Product label — custom description (Production Order) overrides the standard name. */
export const productLabel = (s) => s?.display_name || s?.service_name || s?.name || "";

/** Sort comparator — Production Order sequence (`display_order`), then name. */
export const byProductionOrder = (a, b) => {
  const ao = Number.isFinite(a?.display_order) ? a.display_order : 9999;
  const bo = Number.isFinite(b?.display_order) ? b.display_order : 9999;
  return ao - bo || productLabel(a).localeCompare(productLabel(b));
};
